import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import EditIcon from "@mui/icons-material/Edit";
import ShareIcon from "@mui/icons-material/Share";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Tab from "@mui/material/Tab";
import Divider from "@mui/material/Divider";
import { Button, Tabs, Typography, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Alert, Link, useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import type { HighlightSection } from "../../utils/notificationsApi";
import { RootState } from "../../redux/types/reduxState";
import {
  fetchUserProfile,
  getPersonality,
  getProfileDetails,
  getUserDetails,
  getUserVideos,
  setErrorMessage,
  setSuccessMessage,
  updateUserProfile,
  uploadImage,
  patchProfilePictureInStore,
} from "../../redux/actions/appActions";
import API_MIDDLEWARE from "../../redux/api/middleware";
import { DELETE_USER_PROFILE_PICTURE_URL, FRONTEND_BASE_URL } from "../../redux/api/config";
import {
  ROUTE_CERTIFICATION_NEW,
  ROUTE_CRAFTING_VIDEO,
  ROUTE_EDUCATION_NEW,
  ROUTE_EXPERIENCE_NEW,
  ROUTE_LOOKING_FOR,
  ROUTE_PERSONALITY_PROFILER,
  ROUTE_PROFILER,
  ROUTE_SKILLSET_NEW,
  ROUTE_UPLOAD_PIC,
  ROUTE_WHAT_YOUR_NAME,
  buildCandidateProfilePath
} from "../../constants/routes";
import { APP_CUSTOM_ERROR } from "../../constants/AppVarConstants";
import {
  SET_USER_CERTIFICATE,
  SET_USER_DETAILS,
  SET_USER_EDUCATION,
  SET_USER_EXPERIENCE,
  SET_USER_PREFERENCE,
} from "../../redux/actions/actionTypes";
import moment from "moment";
import JobApplications from "./JobApplications";
import JobRecommendations from "./JobRecommendations";
// import VideoMenu from "./VideoMenu";
import ReactPlayer from "react-player";
import VideoThumbnail from "../common/VideoThumbnail";
import SeeviHeader from "../common/SeeviHeader";
import { ArrowForward, CheckCircle, Cancel, Schedule } from "@mui/icons-material";
import VideoReview from "./VideoReview";
import VideoReplace from "./VideoReplace";
import CandidateDeleteAccount from "./CandidateDeleteAccount";

const options2 = ["VIDEO", "PERSONALITY", "SPECIALIZATION", "PROFILE"];

/** Video status: APPROVED | REJECTED | PENDING (empty or undefined treated as PENDING) */
const getVideoStatus = (el: any): "APPROVED" | "REJECTED" | "PENDING" => {
  const status = (el?.approvalStatus || "").toUpperCase();
  if (status === "APPROVED") return "APPROVED";
  if (status === "REJECTED") return "REJECTED";
  return "PENDING";
};

const VIDEO_STATUS_CONFIG = {
  APPROVED: {
    label: "Approved",
    accentBar: "#4CAF50",
    pillBg: "rgba(76, 175, 80, 0.15)",
    color: "#2E7D32",
    Icon: CheckCircle,
  },
  REJECTED: {
    label: "Rejected",
    accentBar: "#E57373",
    pillBg: "rgba(244, 67, 54, 0.12)",
    color: "#C62828",
    Icon: Cancel,
  },
  PENDING: {
    label: "Under review",
    accentBar: "#FFB74D",
    pillBg: "rgba(255, 152, 0, 0.12)",
    color: "#E65100",
    Icon: Schedule,
  },
};

function CustomTabPanel(props: any) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const NewProfile = ({ title, text, buttonText, route }: any) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ border: "1px solid #756DE9", borderRadius: "10px", p: 0.5 }}>
      <Box sx={{ padding: 1.5 }}>
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        <Typography sx={{ opacity: "0.8", fontSize: "12px", mt: 1 }}>{text}</Typography>
      </Box>
      <Button
        sx={{
          pt: 1.5,
          pb: 1.5,
          backgroundColor: "#756DE9",
          color: "#fff",
          mt: 2,
          width: "100%",
          borderRadius: "0 0 10px 10px",
        }}
        onClick={() => {
          localStorage.setItem("EDIT", "true");
          navigate(route);
        }}
      >
        {buttonText}
      </Button>
    </Box>
  );
};

const NewProfileList = ({ title, list, buttonText, route }: any) => {
  const navigate = useNavigate();
  return (
    <Box sx={{ border: "1px solid #756DE9", borderRadius: "10px", p: 0.5 }}>
      <Box sx={{ padding: 1.5 }}>
        <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
        <ul style={{ paddingLeft: "30px", margin: 0 }}>
          {list.map((el: any) => (
            <li key={el} style={{ listStyleType: "disc", opacity: "0.8" }}>
              <Typography sx={{ fontSize: "12px", mt: 1 }}>{el}</Typography>
            </li>
          ))}
        </ul>
      </Box>
      <Button
        sx={{
          pt: 1.5,
          pb: 1.5,
          backgroundColor: "#756DE9",
          color: "#fff",
          mt: 2,
          width: "100%",
          borderRadius: "0 0 10px 10px",
        }}
        onClick={() => {
          localStorage.setItem("EDIT", "true");
          navigate(route);
        }}
      >
        {buttonText}
      </Button>
    </Box>
  );
};

const CompleteProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const searchParams = new URLSearchParams(routeLocation.search);
  const highlightJobId =
    (routeLocation.state as { highlightJobId?: string } | null)?.highlightJobId ??
    searchParams.get("highlightJobId") ??
    undefined;
  const highlightSection =
    ((routeLocation.state as { highlightSection?: HighlightSection } | null)?.highlightSection ??
      searchParams.get("highlightSection")) as HighlightSection | null | undefined;
  const [, setSelectedFile] = useState(null);
  const [localProfilePreview, setLocalProfilePreview] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteProfilePicDialogOpen, setDeleteProfilePicDialogOpen] = useState(false);
  const [profilePicOptionsDialogOpen, setProfilePicOptionsDialogOpen] = useState(false);
  const [isRemovingProfilePic, setIsRemovingProfilePic] = useState(false);
  const imageUploadInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const [currentActive, setCurrentActive] = useState(0);
  const videoRef = useRef<HTMLDivElement>(null);
  const personalityRef = useRef<HTMLDivElement>(null);
  const skillsRef = useRef<HTMLDivElement>(null);
  const profileRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const user = useSelector((state: any) => state.app.user);
  const userProfile = useSelector((state: RootState) => state.app.userProfile);
  const userExeperience = useSelector((state: RootState) => state.app.userExeperience);
  const userEducation = useSelector((state: RootState) => state.app.userEducation);
  const userPreference = useSelector((state: RootState) => state.app.userPreference);
  const userCertificates = useSelector((state: RootState) => state.app.userCertificates);
  const personality = useSelector((state: RootState) => state.app.personality);
  const userVideos = useSelector((state: RootState) => state.app.userVideos);
  const { fullName, phoneNumber, email, dob, profileStatus, userId, id, gender, profileImage } = user?.user || {};
  const { videoUploaded, videoStatus } = profileStatus || {};
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareBottomSheetOpen, setShareBottomSheetOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  // Use userId or id, whichever is available
  const userProfileId = userId || id;
  const {
    languages,
    tools,
    skills,
    city,
    profilePicturePath,
    workLinks,
    work_links: workLinksSnake,
    workLink,
    work_link: workLinkSnake,
  } = userProfile || {};
  const {
    functionalArea,
    industry,
    jobType,
    workMode,
    minSalary,
    maxSalary,
    city: cities,
    jobRole,
    jobCategory,
  } = userPreference || {};
  const [value, setValue] = React.useState(0);
  const [selectedTab, setSelectedTab] = useState(0);
  const [activitiesTab, setActivitiesTab] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);

  const handleChange = (event: any, newValue: any) => {
    setValue(newValue);
  };

  const handleFileChange = (event: any) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      uploadFile(file);
    }
  };

  const uploadFile = async (file: any) => {
    const previewUrl = URL.createObjectURL(file);
    setLocalProfilePreview(previewUrl);
    setIsLoading(true);
    try {
      const res: any = await new Promise((resolve, reject) => {
        dispatch(
          uploadImage(file, (result) => {
            resolve(result);
          })
        );
      });

      const pictureUrl = res?.result;
      if (pictureUrl) {
        dispatch(patchProfilePictureInStore(pictureUrl));
      }

      await new Promise<void>((resolve, reject) => {
        dispatch(
          updateUserProfile(
            { profilePicturePath: pictureUrl },
            () => resolve(),
            () => reject(new Error("Profile update failed"))
          )
        );
      });
    } catch (error) {
      setLocalProfilePreview(null);
      dispatch(setErrorMessage(APP_CUSTOM_ERROR));
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmRemoveProfilePhoto = () => {
    setIsRemovingProfilePic(true);
    API_MIDDLEWARE(DELETE_USER_PROFILE_PICTURE_URL, "POST", { "Content-Type": "application/json" }, {})
      .then((res: any) => {
        dispatch(setSuccessMessage(res?.message ?? "Profile photo removed."));
        dispatch(patchProfilePictureInStore(""));
        dispatch(fetchUserProfile());
        dispatch(getProfileDetails());
        setDeleteProfilePicDialogOpen(false);
        if (imageUploadInputRef.current) imageUploadInputRef.current.value = "";
      })
      .catch((err: any) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
      })
      .finally(() => {
        setIsRemovingProfilePic(false);
      });
  };

  const handleProfilePicEditClick = () => {
    if (isLoading || isRemovingProfilePic) return;
    setProfilePicOptionsDialogOpen(true);
  };

  const handleChangeProfilePic = () => {
    setProfilePicOptionsDialogOpen(false);
    imageUploadInputRef.current?.click();
  };

  const handleRemoveProfilePicOption = () => {
    setProfilePicOptionsDialogOpen(false);
    setDeleteProfilePicDialogOpen(true);
  };

  const closeProfilePicOptions = () => setProfilePicOptionsDialogOpen(false);
  const closeDeleteProfilePicDialog = () => {
    if (!isRemovingProfilePic) setDeleteProfilePicDialogOpen(false);
  };

  const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => {
        func(...args);
      }, delay);
    };
  };

  const handleTabChange = (event: any, newValue: number) => {
    setIsScrolling(true); // Disable scroll events during programmatic scroll
    setSelectedTab(newValue); // Set the tab to the selected value

    // Scroll to the respective section smoothly
    if (newValue === 0 && videoRef.current) {
      videoRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (newValue === 1 && personalityRef.current) {
      personalityRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (newValue === 2 && skillsRef.current) {
      skillsRef.current?.scrollIntoView({ behavior: "smooth" });
    } else if (newValue === 3 && profileRef.current) {
      profileRef.current?.scrollIntoView({ behavior: "smooth" });
    }

    // Re-enable scroll events after the scroll ends (adjust timeout as needed)
    setTimeout(() => {
      setIsScrolling(false); // Allow manual scroll detection after programmatic scroll ends
    }, 800);
  };

  const handleScroll = debounce(() => {
    if (!containerRef.current) return;

    const scrollPosition = containerRef.current.scrollTop + containerRef.current.offsetHeight * 0.5; // Use 75% of the height
    const videoPos = videoRef.current?.offsetTop || 0;
    const personalityPos = personalityRef.current?.offsetTop || 0;
    const skillsPos = skillsRef.current?.offsetTop || 0;
    const profilePos = profileRef.current?.offsetTop || 0;

    // Ensure the scroll logic only runs when no programmatic scroll is active
    if (!isScrolling) {
      // Adjust the scroll position to check if it has passed a section by a certain margin
      const offsetMargin = 500; // Adjust the margin as needed

      if (scrollPosition >= profilePos - offsetMargin) {
        setSelectedTab(3);
      } else if (scrollPosition >= skillsPos - offsetMargin) {
        setSelectedTab(2);
      } else if (scrollPosition >= personalityPos - offsetMargin) {
        setSelectedTab(1);
      } else if (scrollPosition >= videoPos - offsetMargin) {
        setSelectedTab(0);
      }
    }
  }, 100); // Adjust debounce delay as needed

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isScrolling]);

  useEffect(() => {
    dispatch(fetchUserProfile());
    dispatch(getProfileDetails());
    dispatch(getUserDetails(SET_USER_EDUCATION, "education"));
    dispatch(getUserDetails(SET_USER_CERTIFICATE, "certificates"));
    dispatch(getUserDetails(SET_USER_EXPERIENCE, "experience"));
    dispatch(getUserDetails(SET_USER_PREFERENCE, "preferences"));
    dispatch(getPersonality());
    dispatch(getUserVideos());
    localStorage.removeItem("EDIT");
  }, []);

  useEffect(() => {
    const activeTabFromState = (routeLocation.state as { activeTab?: number } | null)?.activeTab;

    if (highlightJobId || activeTabFromState !== undefined) {
      setValue(1);
    }

    if (activeTabFromState !== undefined && activeTabFromState !== null) {
      setActivitiesTab(activeTabFromState);
    } else if (highlightSection === "recommended") {
      setActivitiesTab(0);
    } else if (highlightSection === "applied") {
      setActivitiesTab(1);
    }
  }, [routeLocation.state, routeLocation.search, highlightJobId, highlightSection]);

  useEffect(() => {
    if (localProfilePreview) return;
    const resolvedPicture = profilePicturePath || profileImage;
    if (resolvedPicture) {
      setIsLoading(true);
      const img = new Image();
      img.onload = () => setIsLoading(false);
      img.onerror = () => setIsLoading(false);
      img.src = resolvedPicture;
    } else {
      setIsLoading(false);
    }
  }, [profilePicturePath, profileImage, localProfilePreview]);

  const resolvedProfilePicture = localProfilePreview || profilePicturePath || profileImage;

  const handleVideoClick = (index: any) => {
    setCurrentActive(index);
  };

  const handleShareProfile = () => {
    if (userProfileId) {
      const shareableUrl = `${FRONTEND_BASE_URL}${buildCandidateProfilePath(userProfileId, fullName, { cacheBust: true })}`;
      setShareLink(shareableUrl);
      setShareModalOpen(true);
    } else {
      // Show error if userId is not available
      alert("Unable to generate share link. Please try again later.");
    }
  };

  const handleCloseShareBottomSheet = () => {
    setShareBottomSheetOpen(false);
  };

  const handleShareWhatsApp = () => {
    const message = `Check out ${fullName}'s SeeVi profile: ${shareLink}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    setShareBottomSheetOpen(false);
  };

  const handleShareFacebook = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareLink)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
    setShareBottomSheetOpen(false);
  };

  const handleShareEmail = () => {
    const subject = `Check out ${fullName}'s SeeVi profile`;
    const body = `Check out ${fullName}'s SeeVi profile: ${shareLink}`;
    const emailUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = emailUrl;
    setShareBottomSheetOpen(false);
  };

  const handleCopyLink = () => {
    if (shareLink) {
      navigator.clipboard.writeText(shareLink).then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      });
    }
  };


  const handleCloseShareModal = () => {
    setShareModalOpen(false);
    setLinkCopied(false);
  };

  const DEFAULT_PROFILE_FILLER = "https://talentsparkstatic.s3.eu-north-1.amazonaws.com/images/Profile_Filler.svg";
  const hasProfilePicture =
    !!resolvedProfilePicture &&
    String(resolvedProfilePicture).trim().length > 0 &&
    String(resolvedProfilePicture).trim() !== DEFAULT_PROFILE_FILLER;

  const profilePicOptionButtons = (
    <>
      <Button
        fullWidth
        variant="outlined"
        onClick={handleChangeProfilePic}
        sx={{
          textTransform: "none",
          fontWeight: 500,
          borderColor: "#756DE9",
          color: "#756DE9",
          py: 1.25,
          "&:hover": { borderColor: "#6159d1", backgroundColor: "rgba(117, 109, 233, 0.08)" },
        }}
      >
        Change picture
      </Button>
      {hasProfilePicture ? (
        <Button
          fullWidth
          variant="outlined"
          onClick={handleRemoveProfilePicOption}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderColor: "#d32f2f",
            color: "#d32f2f",
            py: 1.25,
            "&:hover": { borderColor: "#b71c1c", backgroundColor: "rgba(211, 47, 47, 0.08)" },
          }}
        >
          Remove picture
        </Button>
      ) : null}
    </>
  );

  const resolvedWorkLinks: string[] = (() => {
    const fromArray = workLinks ?? workLinksSnake;
    if (Array.isArray(fromArray)) {
      return fromArray.map((v) => String(v ?? "").trim()).filter(Boolean);
    }
    const fromSingle = workLink ?? workLinkSnake;
    return fromSingle != null && String(fromSingle).trim() !== "" ? [String(fromSingle).trim()] : [];
  })();
  const formatWorkLinkHref = (raw: string) => {
    const t = String(raw).trim();
    if (!t) return "#";
    if (/^https?:\/\//i.test(t)) return t;
    return `https://${t}`;
  };
  const hasSpecializationContent =
    Boolean(skills?.length) ||
    Boolean(tools?.length) ||
    Boolean(userCertificates?.length) ||
    Boolean(resolvedWorkLinks.length);

  return (
    <Box>
      <SeeviHeader />

      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={profilePicOptionsDialogOpen}
          onClose={closeProfilePicOptions}
          PaperProps={{
            sx: {
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
            },
          }}
        >
          <Box sx={{ width: "100%", padding: "20px", paddingBottom: "28px" }}>
            <Box
              sx={{
                width: 40,
                height: 4,
                borderRadius: 2,
                bgcolor: "#E0E0E0",
                mx: "auto",
                mb: 2,
              }}
            />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1F2235" }}>
                Profile photo
              </Typography>
              <IconButton onClick={closeProfilePicOptions} sx={{ padding: "4px" }} aria-label="Close">
                <CloseIcon sx={{ fontSize: "24px", color: "#666" }} />
              </IconButton>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>{profilePicOptionButtons}</Box>
          </Box>
        </Drawer>
      ) : (
        <Dialog
          open={profilePicOptionsDialogOpen}
          onClose={closeProfilePicOptions}
          aria-labelledby="profile-photo-options-title"
          PaperProps={{ sx: { borderRadius: "12px", minWidth: 280 } }}
        >
          <DialogTitle id="profile-photo-options-title" sx={{ fontSize: 18, fontWeight: 600, pb: 1 }}>
            Profile photo
          </DialogTitle>
          <DialogContent sx={{ pt: 0, display: "flex", flexDirection: "column", gap: 1 }}>
            {profilePicOptionButtons}
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2 }}>
            <Button onClick={closeProfilePicOptions} sx={{ textTransform: "none" }}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {isMobile ? (
        <Drawer
          anchor="bottom"
          open={deleteProfilePicDialogOpen}
          onClose={closeDeleteProfilePicDialog}
          PaperProps={{
            sx: {
              borderTopLeftRadius: "20px",
              borderTopRightRadius: "20px",
            },
          }}
        >
          <Box sx={{ width: "100%", padding: "20px", paddingBottom: "28px" }}>
            <Box
              sx={{
                width: 40,
                height: 4,
                borderRadius: 2,
                bgcolor: "#E0E0E0",
                mx: "auto",
                mb: 2,
              }}
            />
            <Typography sx={{ fontSize: "18px", fontWeight: 600, color: "#1F2235", mb: 1 }}>
              Remove profile photo?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
              Your profile picture will be removed. You can upload a new one anytime.
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Button
                fullWidth
                variant="contained"
                color="error"
                onClick={handleConfirmRemoveProfilePhoto}
                disabled={isRemovingProfilePic}
                sx={{ textTransform: "none", fontWeight: 600, py: 1.25 }}
              >
                {isRemovingProfilePic ? <CircularProgress size={22} color="inherit" /> : "Remove"}
              </Button>
              <Button
                fullWidth
                variant="outlined"
                onClick={closeDeleteProfilePicDialog}
                disabled={isRemovingProfilePic}
                sx={{ textTransform: "none", py: 1.25 }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Drawer>
      ) : (
        <Dialog
          open={deleteProfilePicDialogOpen}
          onClose={closeDeleteProfilePicDialog}
          aria-labelledby="delete-profile-photo-title"
        >
          <DialogTitle id="delete-profile-photo-title">Remove profile photo?</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              Your profile picture will be removed. You can upload a new one anytime.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
            <Button onClick={closeDeleteProfilePicDialog} disabled={isRemovingProfilePic}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmRemoveProfilePhoto}
              color="error"
              variant="contained"
              disabled={isRemovingProfilePic}
              sx={{ textTransform: "none" }}
            >
              {isRemovingProfilePic ? <CircularProgress size={22} color="inherit" /> : "Remove"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Box sx={{ backgroundColor: "#ffffff", pt: 4, px: 4 }}>
        <Stack direction="row" flexWrap="wrap">
          <Stack direction="column" alignItems="start" spacing={1} sx={{ width: "100%" }}>
             <Box sx={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
               <div className="avatar-upload">
                 <div className="avatar-edit">
                   <input
                     ref={imageUploadInputRef}
                     capture={false}
                     type="file"
                     id="imageUpload"
                     onChange={handleFileChange}
                     accept=".png, .jpg, .jpeg"
                   />
                   <label
                     htmlFor="imageUpload"
                     onClick={(e) => {
                       e.preventDefault();
                       handleProfilePicEditClick();
                     }}
                   >
                     <EditIcon style={{ fontSize: 20, color: "#827BEA" }} />
                   </label>
                 </div>
                 <div className="avatar-preview">
                   <div id="imagePreview" style={{ backgroundImage: `url(${resolvedProfilePicture})` }}></div>
                   {isLoading && (
                     <Box
                       sx={{
                         position: "absolute",
                         top: 0,
                         left: 0,
                         width: "100%",
                         height: "100%",
                         display: "flex",
                         alignItems: "center",
                         justifyContent: "center",
                         backgroundColor: "rgba(0, 0, 0, 0.4)",
                         borderRadius: "50%",
                       }}
                     >
                       <CircularProgress sx={{ color: "#fff" }} />
                     </Box>
                   )}
                 </div>
               </div>
               <Button
                 variant="contained"
                 onClick={handleShareProfile}
                 sx={{
                   backgroundColor: "#FF8960",
                   color: "#ffffff",
                   textTransform: "none",
                   borderRadius: "8px",
                   padding: "6px 16px",
                   fontSize: "14px",
                   fontWeight: 500,
                   minHeight: "36px",
                   height: "36px",
                   "&:hover": {
                     backgroundColor: "#ff7848",
                   },
                 }}
               >
                 Share your SeeVi
               </Button>
             </Box>

            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ width: "100%" }}>
              <h4 className="text-dark mb-0" style={{ fontSize: "22px" }}>
                {fullName}
              </h4>
              <Stack direction="row" alignItems="center" spacing={1}>
              
                <Typography
                  sx={{
                    color: "#756DE9",
                    fontSize: "14px",
                    fontWeight: "550",
                    cursor: "pointer",
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  }}
                  onClick={() => {
                    navigate(`${ROUTE_WHAT_YOUR_NAME}?edit`);
                  }}
                >
                  Edit Profile
                </Typography>
              </Stack>
            </Stack>

            <div className="mt-1">
              <Stack direction="row" alignItems="center" sx={{ width: "100%" }}>
                <img src="images/Call-icon.png" width="20px"></img>
                <span style={{ fontSize: "14px", color: "#777777", fontWeight: "550", marginLeft: "10px" }}>
                  {phoneNumber}
                </span>
              </Stack>
            </div>
            <div className="mt-2">
              <Stack direction="row" alignItems="center">
                <img src="images/Email_icon.png" width="20px"></img>
                <span style={{ fontSize: "14px", color: "#777777", fontWeight: "550", marginLeft: "10px" }}>
                  {email}
                </span>
              </Stack>
            </div>
            <CandidateDeleteAccount phoneNumber={phoneNumber} />
          </Stack>
        </Stack>
      </Box>

      <Container maxWidth="sm">
        <Box sx={{ width: "100%" }} className="px-lg-4 mt-4">
          <Box sx={{ width: "100%", typography: "body1" }}>
            <Box style={{ backgroundColor: "rgb(238 239 243)", borderRadius: "99px" }}>
              <Tabs
                value={value}
                onChange={handleChange}
                aria-label="lab API tabs example"
                sx={{
                  "& .MuiTabs-indicator": {
                    display: "none",
                  },
                }}
              >
                <Tab
                  sx={{
                    flex: 1,
                    "&.Mui-selected": {
                      fontWeight: "500",
                      border: "none !important",
                      backgroundColor: "rgb(255, 137, 96)",
                      borderRadius: "99px",
                      color: "#ffffff",
                      fontSize: "16px",
                      padding: "0px 20px",
                      whiteSpace: "nowrap", // Prevent text from wrapping
                    },
                    textTransform: "capitalize",
                    fontSize: "16px",
                  }}
                  label="MY SEEVI"
                  {...a11yProps(0)}
                />
                <Tab
                  sx={{
                    flex: 1,
                    "&.Mui-selected": {
                      fontWeight: "500",
                      border: "none !important",
                      backgroundColor: "rgb(255, 137, 96)",
                      borderRadius: "99px",
                      color: "#ffffff",
                      fontSize: "16px",
                      padding: "0px 30px",
                      whiteSpace: "nowrap", // Prevent text from wrapping
                    },
                    textTransform: "capitalize",
                    fontSize: "16px",
                  }}
                  label="MY ACTIVITIES"
                  {...a11yProps(1)}
                />
              </Tabs>
            </Box>
            <CustomTabPanel value={value} index={0}>
              <Box sx={{ overflowX: "auto", mt: 2 }}>
                <Tabs
                  variant="scrollable"
                  indicatorColor="primary"
                  value={selectedTab}
                  onChange={handleTabChange}
                  sx={{
                    "& .MuiTabs-indicator": {
                      opacity: "1 !important",
                      height: "2px",
                      backgroundColor: "#756DE9 !important",
                      borderRadius: "5px",
                      top: 40,
                      bottom: "auto",
                      transform: "translateY(-100%)",
                      width: "100%",
                      color: "#6352CC",
                    },
                    "& .MuiTab-root": {
                      minWidth: "auto",
                      padding: "2px 10px",
                      fontSize: "14px",
                      textTransform: "uppercase",
                      whiteSpace: "nowrap",
                    },
                    ".Mui-selected": {
                      color: "#6352CC",
                      fontWeight: "600",
                      border: "none",
                      fontSize: "14px",
                      textTransform: "uppercase",
                    },
                  }}
                >
                  {options2.map((degree, index) => (
                    <Tab key={index} label={degree} />
                  ))}
                </Tabs>
                <Box
                  ref={containerRef}
                  sx={{
                    flexGrow: 1,
                    overflowY: "auto",
                    maxHeight: "45vh",
                    mt: 2,
                  }}
                >
                  <Stack direction="column">
                    {videoStatus === "INACTIVE" && videoUploaded ? (
                      <VideoReview sections={userVideos} showEdit={true} />
                    ) : userVideos?.length === 0 ? (
                      <Box ref={videoRef}>
                        <NewProfile
                          title="Create Video"
                          text="Showcase your real self with a video and create a strong first impression on potential employers."
                          buttonText="Create Now"
                          route={ROUTE_CRAFTING_VIDEO}
                        />
                      </Box>
                    ) : userVideos?.length ? (
                      userVideos?.map((el: any, index: number) => {
                        const status = getVideoStatus(el);
                        const statusConfig = VIDEO_STATUS_CONFIG[status];
                        const StatusIcon = statusConfig.Icon;
                        if (currentActive === index) {
                          const showStatusUi = status !== "APPROVED";
                          return (
                            <React.Fragment key={index}>
                              <Box
                                ref={videoRef}
                                sx={{
                                  borderRadius: 1.5,
                                  ...(showStatusUi && {
                                    borderLeft: `4px solid ${statusConfig.accentBar}`,
                                    pl: 1.5,
                                    pr: 1.5,
                                    py: 1,
                                  }),
                                  mb: 1,
                                }}
                              >
                                {showStatusUi && (
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: 0.5,
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: "99px",
                                      backgroundColor: statusConfig.pillBg,
                                      mb: 1,
                                    }}
                                  >
                                    <StatusIcon sx={{ fontSize: 14, color: statusConfig.color }} />
                                    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: statusConfig.color }}>
                                      {statusConfig.label}
                                    </Typography>
                                  </Box>
                                )}
                                <Grid container spacing={2} alignItems="center">
                                  <Grid item xs={12} md={3} p={1}>
                                    <div>
                                      <ReactPlayer
                                        height={200}
                                        url={el?.videoPath}
                                        width="100%"
                                        controls
                                        playing={false}
                                      />
                                    </div>
                                  </Grid>
                                  <Grid item xs={9}>
                                    <div className="position-relative mb-2">
                                      <Typography className="custom-typography-chat">{el?.topic}</Typography>
                                      <Typography style={{ fontSize: "12px" }}>{el?.question}</Typography>
                                    </div>
                                  </Grid>

                                  <Grid item xs={3} sx={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                    <VideoReplace questionId={el?.questionId} />
                                  </Grid>
                                </Grid>
                              </Box>
                              {userVideos?.length - 1 === index ? null : <Divider variant="middle" />}
                            </React.Fragment>
                          );
                        }
                        const showStatusUi = status !== "APPROVED";
                        return (
                          <React.Fragment key={index}>
                            <Box
                              onClick={() => handleVideoClick(index)}
                              sx={{
                                borderRadius: 1.5,
                                ...(showStatusUi && {
                                  borderLeft: `4px solid ${statusConfig.accentBar}`,
                                  pl: 1,
                                  pr: 1,
                                  py: 1,
                                }),
                                mb: 2,
                                cursor: "pointer",
                              }}
                            >
                              {showStatusUi && (
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: "99px",
                                    backgroundColor: statusConfig.pillBg,
                                    mb: 0.75,
                                  }}
                                >
                                  <StatusIcon sx={{ fontSize: 12, color: statusConfig.color }} />
                                  <Typography sx={{ fontSize: "10px", fontWeight: 600, color: statusConfig.color }}>
                                    {statusConfig.label}
                                  </Typography>
                                </Box>
                              )}
                              <Grid container spacing={2} alignItems="center">
                                <Grid item xs={4}>
                                  <div className="position-relative">
                                    <VideoThumbnail videoUrl={el?.videoPath} width="100%" height="90px" />
                                  </div>
                                </Grid>
                                <Grid item xs={5}>
                                  <Stack direction="column" className="position-relative">
                                    <Typography className="custom-typography-chat">{el?.topic}</Typography>
                                    <Typography style={{ fontSize: "12px" }}>{el?.question}</Typography>
                                  </Stack>
                                </Grid>
                                <Grid item xs={3} sx={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                                  <VideoReplace questionId={el?.questionId} />
                                </Grid>
                              </Grid>
                            </Box>

                            {userVideos?.length - 1 === index ? null : <Divider variant="middle" />}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <Button
                        sx={{
                          backgroundColor: "#756DE9",
                          borderRadius: "99px",
                          fontSize: "16px",
                          textTransform: "capitalize",
                          width: "100%",
                          paddingY: "15px",
                          paddingX: 0,
                          mt: 2,
                          "&:hover": {
                            backgroundColor: "#756DE9",
                          },
                        }}
                        variant="contained"
                        size="small"
                        className="w-100 mt-2 btn-pad"
                        onClick={() => navigate(ROUTE_CRAFTING_VIDEO)}
                      >
                        Let&apos; Upload Videos
                      </Button>
                    )}
                  </Stack>

                  {personality?.length <= 0 ? (
                    <Box ref={personalityRef} mt={3}>
                      <NewProfile
                        title="Find Your Personality"
                        text="Discover valuable insights about yourself that will help you choose the right career path."
                        buttonText="Find Out Now"
                        route={ROUTE_PROFILER}
                      />
                    </Box>
                  ) : (
                    <>
                      <Box
                        component="h4"
                        ref={personalityRef}
                        sx={{
                          color: "#1F2235",
                          fontSize: "20px",
                          fontWeight: "550",
                          textAlign: "center",
                          marginTop: "20px",
                        }}
                      >
                        Personality
                      </Box>

                      <Box
                        sx={{
                          borderRadius: "10px",
                          backgroundColor: "#ffffff",
                          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
                        }}
                        className="pb-1"
                      >
                        {personality?.map((el: any, index: any) => {
                          return (
                            <Stack
                              key={index}
                              className="m-0"
                              direction={{ xs: "row", sm: "row" }}
                              alignItems="center"
                              sx={{ px: 3, pb: 3 }}
                              justifyContent="space-between"
                            >
                              <Box>
                                <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                                  <Box component="img" sx={{ height: 150 }} src={el?.imagePath} />
                                </Box>

                                <Box
                                  component="h3"
                                  sx={{ color: "#000000", fontSize: "18px", fontWeight: "550", mb: 2 }}
                                >
                                  {el?.displayPersonalityType}
                                </Box>
                                <Typography sx={{ fontSize: "12px" }}>{el?.typeDescriptors}</Typography>
                              </Box>
                            </Stack>
                          );
                        })}

                        <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            fontWeight: 600,
                            fontSize: "14px",
                            color: "#756DE9",
                            cursor: "pointer",
                            gap: 1,
                            px: 2,
                            pb: 1,
                          }}
                          onClick={() => navigate(ROUTE_PERSONALITY_PROFILER)}
                        >
                          View Report <ArrowForward sx={{ fontSize: "18px" }} />
                        </Typography>
                      </Box>
                    </>
                  )}
                  {!hasSpecializationContent ? (
                    <Box my={3}>
                      <NewProfileList
                        title="Add Specialization"
                        list={["Skill Sets", "Tools Known", "Work link (optional)", "Certifications if any"]}
                        buttonText="Add Now"
                        route={ROUTE_SKILLSET_NEW}
                      />
                    </Box>
                  ) : (
                    <>
                      <Box
                        component="h4"
                        ref={skillsRef}
                        sx={{
                          color: "#1F2235",
                          fontSize: "20px",
                          fontWeight: "550",
                          textAlign: "center",
                          marginTop: "20px",
                        }}
                      >
                        Specialization
                      </Box>
                      <Box sx={{ flexGrow: 1 }} className="mt-3">
                        <Box
                          sx={{
                            borderRadius: "10px",
                            border: "1px solid #B7B3EB",
                            padding: "2px",
                            marginBottom: "10px",
                          }}
                        >
                          <Stack
                            style={{
                              backgroundColor: "#e5e3fd",
                              height: "20px",
                              padding: "10px",
                              borderRadius: "10px 10px 0px 0px",
                            }}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                              Skill Set
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <Typography
                                onClick={() => navigate(`${ROUTE_SKILLSET_NEW}?edit`)}
                                style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}
                              >
                                Edit
                              </Typography>
                            </Stack>
                          </Stack>
                          <Stack style={{ padding: "10px" }} direction="column">
                            <Typography style={{ color: "#555555", fontSize: "14px" }}>
                              {skills?.map((tool: string, index: number) => (
                                <React.Fragment key={index}>
                                  <span>{tool}</span>
                                  {index !== skills.length - 1 && <span style={{ color: "orange" }}> | </span>}
                                </React.Fragment>
                              ))}
                            </Typography>
                          </Stack>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: "10px",
                            border: "1px solid #B7B3EB",
                            padding: "2px",
                            marginBottom: "10px",
                          }}
                        >
                          <Stack
                            style={{
                              backgroundColor: "#e5e3fd",
                              height: "20px",
                              padding: "10px",
                              borderRadius: "10px 10px 0px 0px",
                            }}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                              Tools Known
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <Typography
                                onClick={() => navigate(`${ROUTE_SKILLSET_NEW}?edit`)}
                                style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}
                              >
                                Edit
                              </Typography>
                            </Stack>
                          </Stack>
                          <Stack style={{ padding: "10px" }} direction="column">
                            <Typography style={{ color: "#555555", fontSize: "14px" }}>
                              {tools?.map((tool: string, index: number) => (
                                <React.Fragment key={index}>
                                  <span>{tool}</span>
                                  {index !== tools.length - 1 && <span style={{ color: "orange" }}> | </span>}
                                </React.Fragment>
                              ))}
                            </Typography>
                          </Stack>
                        </Box>

                        <Box
                          sx={{
                            borderRadius: "10px",
                            border: "1px solid #B7B3EB",
                            padding: "2px",
                            marginBottom: "10px",
                          }}
                        >
                          <Stack
                            style={{
                              backgroundColor: "#e5e3fd",
                              height: "20px",
                              padding: "10px",
                              borderRadius: "10px 10px 0px 0px",
                            }}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                              Work link
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <Typography
                                onClick={() => navigate(`${ROUTE_SKILLSET_NEW}?edit`)}
                                style={{ color: "#000000", fontSize: "14px", fontWeight: "550", cursor: "pointer" }}
                              >
                                Edit
                              </Typography>
                            </Stack>
                          </Stack>
                          <Stack style={{ padding: "10px" }} direction="column">
                            {resolvedWorkLinks.length ? (
                              <Stack direction="column" spacing={0.75}>
                                {resolvedWorkLinks.map((wl, i) => (
                                  <Link
                                    key={`${wl}-${i}`}
                                    href={formatWorkLinkHref(wl)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ fontSize: "14px", color: "#756DE9", wordBreak: "break-all" }}
                                  >
                                    {wl}
                                  </Link>
                                ))}
                              </Stack>
                            ) : (
                              <Typography style={{ color: "#999999", fontSize: "14px", fontStyle: "italic" }}>
                                Not added
                              </Typography>
                            )}
                          </Stack>
                        </Box>

                        <Box
                          style={{
                            borderRadius: "10px",
                            border: "1px solid #B7B3EB",
                            padding: "2px",
                            marginBottom: "10px",
                          }}
                        >
                          <Stack
                            style={{
                              backgroundColor: "#e5e3fd",
                              height: "20px",
                              padding: "10px",
                              borderRadius: "10px 10px 0px 0px",
                            }}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                              Certifications
                            </Typography>
                            <Stack direction="row" spacing={1}>
                              <Typography
                                onClick={() => navigate(`${ROUTE_CERTIFICATION_NEW}?edit`)}
                                style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}
                              >
                                Edit
                              </Typography>
                            </Stack>
                          </Stack>
                          <Stack style={{ padding: "10px" }} direction="column">
                            {userCertificates?.map((el: any, index: number) => {
                              return (
                                <React.Fragment key={index}>
                                  <Typography
                                    style={{
                                      color: "#000000",
                                      fontSize: "14px",
                                      fontWeight: "550",
                                      margin: "10px 0px",
                                    }}
                                  >
                                    {el?.certificateName}
                                  </Typography>

                                  <Stack direction="column" spacing={1}>
                                    <Stack direction="row" spacing={1}>
                                      <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                        {el?.institute}
                                      </Typography>
                                    </Stack>
                                    <Stack direction="row" spacing={1}>
                                      <img src="images/Calendar-new.png" width="20px"></img>
                                      <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                        Duration: {el?.courseDuration}
                                      </Typography>
                                    </Stack>
                                    {el?.courseExpiry ? (
                                      <Stack direction="row" spacing={1}>
                                        <img src="images/complete-profile/Course Expiry Date.svg" width="20px"></img>
                                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                          Course Expiry Date: {moment(el?.courseExpiry)?.format("DD/MM/YYYY")}
                                        </Typography>
                                      </Stack>
                                    ) : null}

                                    {el?.certificateReceived && (
                                      <Stack direction="row" spacing={1}>
                                        <img src="images/Tick.png" width="20px"></img>
                                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                          Received Certificate
                                        </Typography>
                                      </Stack>
                                    )}
                                    {index < userCertificates.length - 1 && (
                                      <Divider sx={{ mt: 2, mb: 1 }} variant="middle" />
                                    )}
                                  </Stack>
                                </React.Fragment>
                              );
                            })}
                          </Stack>
                        </Box>
                      </Box>
                    </>
                  )}

                  {!userEducation?.length && !userExeperience?.length ? (
                    <Box ref={profileRef} mt={2} mb={2}>
                      <NewProfileList
                        title="Profile Details"
                        list={["Education", "Work Experience"]}
                        buttonText="Add Now"
                        route={ROUTE_EDUCATION_NEW}
                      />
                    </Box>
                  ) : (
                    <>
                      <Box
                        component="h4"
                        ref={profileRef}
                        id="profile"
                        sx={{
                          color: "#1F2235",
                          fontSize: "20px",
                          fontWeight: "550",
                          textAlign: "center",
                          marginTop: "20px",
                        }}
                      >
                        Profile Details
                      </Box>
                    </>
                  )}

                  <Box
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid #B7B3EB",
                      padding: "2px",
                      marginBottom: "10px",
                      mt: 3,
                    }}
                  >
                    <Stack
                      sx={{
                        backgroundColor: "#e5e3fd",
                        height: "20px",
                        padding: "10px",
                        borderRadius: "10px 10px 0px 0px",
                      }}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                        Demographics
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Typography
                          onClick={() => navigate(`${ROUTE_UPLOAD_PIC}?edit`)}
                          style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}
                        >
                          Edit
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack style={{ padding: "10px" }} direction="column">
                      <Stack direction="column" spacing={1}>
                        <Stack direction="row" spacing={1}>
                          <img src="images/dob.png" width="20px"></img>
                          <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                            Date of Birth: {moment(dob).format("DD/MM/YYYY")}
                          </Typography>
                        </Stack>
                        {gender && (
                          <Stack direction="row" spacing={1}>
                            <img src="images/complete-profile/Gender.svg" width="20px"></img>
                            <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                              Gender: {gender}
                            </Typography>
                          </Stack>
                        )}
                        <Stack direction="row" spacing={1}>
                          <img src="images/language.png" width="20px"></img>
                          <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                            Language: {languages?.join(", ")}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1}>
                          <img src="images/location.png" width="20px"></img>
                          <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                            City: {city}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Box>
                  {!userEducation?.length && !userExeperience?.length ? null : (
                    <>
                      <Box
                        style={{
                          borderRadius: "10px",
                          border: "1px solid #B7B3EB",
                          padding: "2px",
                          marginBottom: "10px",
                        }}
                      >
                        <Stack
                          style={{
                            backgroundColor: "#e5e3fd",
                            height: "20px",
                            padding: "10px",
                            borderRadius: "10px 10px 0px 0px",
                          }}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                            Education
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Typography
                              onClick={() => navigate(`${ROUTE_EDUCATION_NEW}?edit`)}
                              style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}
                            >
                              Edit
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack style={{ padding: "10px" }} direction="column">
                          {userEducation?.map((el: any, index: number) => {
                            return (
                              <React.Fragment key={index}>
                                <Stack direction="column" spacing={1}>
                                  {el?.educationType === "Board" ? (
                                    <>
                                      <Stack direction="row" spacing={1}>
                                        <img src="images/complete-profile/Degree.svg" width="20px" alt="board" />
                                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                          {`Board: ${el?.board}`}
                                        </Typography>
                                      </Stack>
                                      <Stack direction="row" spacing={1}>
                                        <img src="images/complete-profile/Industry.svg" style={{ marginLeft: "-2px" }} width="24px" alt="school" />
                                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                          {`School: ${el?.schoolName}`}
                                        </Typography>
                                      </Stack>
                                    </>
                                  ) : (
                                    <>
                                      <Stack direction="row" spacing={1}>
                                        <img src="images/complete-profile/Degree.svg" width="20px" alt="degree" />
                                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                          {`Degree: ${el?.degree}`}
                                        </Typography>
                                      </Stack>
                                      <Stack direction="row" spacing={1}>
                                        <img src="images/complete-profile/Degree.svg" width="20px" alt="course" />
                                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                          {`Course: ${el?.courseName}`}
                                        </Typography>
                                      </Stack>
                                    </>
                                  )}
                                  {el?.specialization ? (
                                    <>
                                      <Stack direction="row" spacing={1}>
                                        <img src="images/complete-profile/Degree.svg" width="20px" alt="specialization" />
                                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                          {el?.courseName ? `Specialization: ${el?.specialization}` : null}
                                        </Typography>
                                      </Stack>
                                    </>
                                  ) : null}

                                  {el?.educationType === "Board" ? null : el?.institutionName ? (
                                    <>
                                      <Stack direction="row" spacing={1}>
                                        <img src="images/complete-profile/Industry.svg" style={{ marginLeft: "-2px" }} width="24px" alt="institution" />
                                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                          {el?.institutionName ? `Institute: ${el?.institutionName}` : null}
                                        </Typography>
                                      </Stack>
                                    </>
                                  ) : null}


                                  <Stack direction="row" spacing={1}>
                                    <img src="images/Calendar-new.png" width="20px"></img>
                                    {el?.board ? (
                                      <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                        Completion Year: {el?.endDate}
                                      </Typography>
                                    ) : (
                                      <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                        Duration:{" "}
                                        {el?.yetToGraduate
                                          ? `${el?.startDate}, Currently Studying`
                                          : `${el?.startDate} - ${el?.endDate}`}
                                      </Typography>
                                    )}
                                  </Stack>
                                  {el?.grades ? (
                                    <>
                                      <Stack direction="row" spacing={1}>
                                        <img src="images/Grades.png" width="20px"></img>
                                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                          {el?.grades ? `Grades: ${el?.grades}` : null}
                                        </Typography>
                                      </Stack>
                                    </>
                                  ) : null}

                                  {el?.gpa ? (
                                    <>
                                      <Stack direction="row" spacing={1}>
                                        <img src="images/Grades.png" width="20px"></img>
                                        <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                          {el?.gpa ? `GPA: ${el?.gpa}` : null}
                                        </Typography>
                                      </Stack>
                                    </>
                                  ) : null}
                                </Stack>
                                {index < userEducation.length - 1 && <Divider sx={{ mt: 2, mb: 1 }} variant="middle" />}
                              </React.Fragment>
                            );
                          })}
                        </Stack>
                      </Box>

                      <Box
                        sx={{
                          borderRadius: "10px",
                          border: "1px solid #B7B3EB",
                          padding: "2px",
                          marginBottom: "10px",
                        }}
                      >
                        <Stack
                          style={{
                            backgroundColor: "#e5e3fd",
                            height: "20px",
                            padding: "10px",
                            borderRadius: "10px 10px 0px 0px",
                          }}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                        >
                          <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                            Experience
                          </Typography>
                          <Stack direction="row" spacing={1}>
                            <Typography
                              onClick={() => navigate(`${ROUTE_EXPERIENCE_NEW}?edit`)}
                              style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}
                            >
                              Edit
                            </Typography>
                          </Stack>
                        </Stack>
                        <Stack style={{ padding: "10px" }} direction="column">
                          {userExeperience?.map((el: any, index: number) => {
                            return (
                              <React.Fragment key={index}>
                                <Stack direction="column" spacing={1}>
                                  <Stack direction="row" spacing={1}>
                                    <Typography style={{ color: "#555555", fontSize: "16px", fontWeight: "550" }}>
                                      {el?.designationName}
                                    </Typography>
                                  </Stack>
                                  <Stack direction="row" spacing={1}>
                                    <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                      {el?.companyName}
                                    </Typography>
                                  </Stack>
                                  {el?.departmentName ? (
                                    <Stack direction="row" spacing={1}>
                                      <img src="images/degree.png" width="20px"></img>
                                      <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                        Function: {el?.departmentName}
                                      </Typography>
                                    </Stack>
                                  ) : null}
                                  {el?.jobType ? (
                                    <Stack direction="row" spacing={1}>
                                      <img src="images/complete-profile/Job Type.svg" width="20px"></img>
                                      <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                        Job Type:{" "}
                                        {typeof el?.jobType === "string" ? el?.jobType : el?.jobType?.join(", ")}
                                      </Typography>
                                    </Stack>
                                  ) : null}
                                  {el?.workMode ? (
                                    <Stack direction="row" spacing={1}>
                                      <img src="images/complete-profile/Job Mode.svg" width="20px"></img>
                                      <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                        Work Mode:{" "}
                                        {typeof el?.workMode === "string" ? el?.workMode : el?.workMode?.join(", ")}
                                      </Typography>
                                    </Stack>
                                  ) : null}

                                  <Stack direction="row" spacing={1} alignItems="flex-start">
                                    <img
                                      src="images/Calendar-new.png"
                                      style={{ marginTop: "4px" }}
                                      height="20px"
                                      width="20px"
                                    ></img>
                                    <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                      {!el?.endDate
                                        ? `Duration:  ${moment(el?.startDate).format(
                                          "DD/MM/YYYY"
                                        )} - Currently working here`
                                        : `Duration: ${moment(el?.startDate).format("DD/MM/YYYY")} - ${moment(
                                          el?.endDate
                                        ).format("DD/MM/YYYY")}`}
                                    </Typography>
                                  </Stack>
                                  {el?.workLink ? (
                                    <Stack direction="row" spacing={1}>
                                      <img src="images/browse-jobs/link-round.png" width="20px"></img>
                                      <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                                        Work Link: {el?.workLink}
                                      </Typography>
                                    </Stack>
                                  ) : null}
                                  <Box>
                                    {el?.description ? (
                                      <Typography style={{ color: "#555555", fontSize: "14px" }}>
                                        {el?.description}
                                      </Typography>
                                    ) : null}
                                  </Box>
                                </Stack>
                                {index < userExeperience.length - 1 && (
                                  <Divider sx={{ mt: 2, mb: 1 }} variant="middle" />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </Stack>
                      </Box>
                    </>
                  )}

                  <Box
                    sx={{
                      borderRadius: "10px",
                      border: "1px solid #B7B3EB",
                      padding: "2px",
                      marginBottom: "30px",
                      mt: 3,
                    }}
                  >
                    <Stack
                      style={{
                        backgroundColor: "#e5e3fd",
                        padding: "10px",
                        borderRadius: "10px 10px 0px 0px",
                      }}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="column" spacing={1}>
                        <Typography style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}>
                          Preferences
                        </Typography>
                      </Stack>

                      <Stack direction="row" spacing={1}>
                        <Typography
                          onClick={() => navigate(`${ROUTE_LOOKING_FOR}?edit`)}
                          style={{ color: "#000000", fontSize: "14px", fontWeight: "550" }}
                        >
                          Edit
                        </Typography>
                      </Stack>
                    </Stack>
                    <Stack style={{ padding: "10px 0 0 10px" }} direction="column" spacing={1}>
                      <Typography style={{ color: "#555555", fontSize: "16px", fontWeight: "550" }}>
                        {jobCategory}
                      </Typography>
                    </Stack>
                    <Stack style={{ padding: "10px" }} direction="column">
                      <Stack direction="column" spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <img src="images/complete-profile/Functions.svg" width="20px"></img>
                          <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                            Function: {functionalArea?.join(", ")}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <img src="images/complete-profile/Job Mode.svg" width="20px"></img>
                          <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                            Job Role: {Array.isArray(jobRole) ? jobRole.join(", ") : jobRole ?? ""}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="flex-start">
                          <img
                            src="images/complete-profile/Industry.svg"
                            alt="degree"
                            style={{ width: "20px", height: "20px", marginTop: "2px" }}
                          />
                          <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                            Industry: {industry?.join(", ")}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <img src="images/complete-profile/Job Type.svg" width="20px"></img>
                          <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                            Job Type: {typeof jobType === "string" ? jobType : jobType?.join(", ")}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <img src="images/complete-profile/Job Mode.svg" width="20px"></img>
                          <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                            Work mode: {typeof workMode === "string" ? workMode : workMode?.join(", ")}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <img src="images/complete-profile/CTC.svg" width="20px"></img>
                          <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                            Expected CTC: {minSalary} to {maxSalary}
                          </Typography>
                        </Stack>

                        <Stack direction="row" spacing={1} alignItems="center">
                          <img src="images/location.png" width="20px"></img>
                          <Typography style={{ color: "#555555", fontSize: "14px", fontWeight: "550" }}>
                            Cities Preferred: {cities?.join(", ")}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Stack>
                  </Box>
                </Box>
              </Box>
            </CustomTabPanel>
            <CustomTabPanel value={value} index={1}>
            <JobApplications
              highlightJobId={highlightJobId}
              highlightSection={highlightSection ?? undefined}
            />
              {/* <Box sx={{ mt: 2 }}>
                <Box
                  sx={{
                    display: "flex",
                    gap: "32px",
                  }}
                >
                  <Box
                    onClick={() => setActivitiesTab(0)}
                    sx={{
                      paddingBottom: "12px",
                      cursor: "pointer",
                      color: activitiesTab === 0 ? "#000000" : "#999999",
                      fontSize: "14px",
                      fontWeight: activitiesTab === 0 ? "600" : "400",
                      transition: "all 0.3s ease",
                      whiteSpace: "nowrap",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: "-1px",
                        left: 0,
                        right: 0,
                        height: "2px",
                        backgroundColor: activitiesTab === 0 ? "#756DE9" : "transparent",
                        transition: "background-color 0.3s ease",
                      },
                    }}
                  >
                    Recommended
                  </Box>
                  <Box
                    onClick={() => setActivitiesTab(1)}
                    sx={{
                      paddingBottom: "12px",
                      cursor: "pointer",
                      color: activitiesTab === 1 ? "#000000" : "#999999",
                      fontSize: "14px",
                      fontWeight: activitiesTab === 1 ? "600" : "400",
                      transition: "all 0.3s ease",
                      whiteSpace: "nowrap",
                      position: "relative",
                      "&::after": {
                        content: '""',
                        position: "absolute",
                        bottom: "-1px",
                        left: 0,
                        right: 0,
                        height: "2px",
                        backgroundColor: activitiesTab === 1 ? "#756DE9" : "transparent",
                        transition: "background-color 0.3s ease",
                      },
                    }}
                  >
                    Applied
                  </Box>
                </Box>
                <Box 
                  sx={{ 
                    mt: activitiesTab === 0 ? 4 : 0
                  }}
                >
                  {activitiesTab === 0 && <JobRecommendations />}
                  {activitiesTab === 1 && <JobApplications />}
                </Box>
              </Box> */}
            </CustomTabPanel>
          </Box>
        </Box>
      </Container>

      {/* Share Profile Modal */}
      <Dialog
        open={shareModalOpen}
        onClose={handleCloseShareModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "18px",
            padding: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            color: "#1F2235",
            pb: 1,
          }}
        >
          Share Your SeeVi
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#555555",
              mb: 2,
            }}
          >
            Share this link to let others view your profile:
          </Typography>
          {!hasProfilePicture && (
            <Box
              sx={{
                mb: 2,
                borderRadius: "12px",
                background: "linear-gradient(135deg, rgba(117,109,233,0.12) 0%, rgba(117,109,233,0.06) 100%)",
                border: "1px solid rgba(117,109,233,0.25)",
                padding: "14px 16px",
                display: "flex",
                alignItems: "flex-start",
                gap: "12px",
              }}
            >
              <Box
                sx={{
                  width: "36px",
                  height: "36px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(117,109,233,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: "18px",
                }}
              >
                📸
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#1F2235",
                    mb: 0.5,
                  }}
                >
                  Boost your profile visibility & trust
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: "#555555",
                    lineHeight: 1.5,
                    mb: 1.2,
                  }}
                >
                  Profiles with a photo get significantly more views. Add yours to make a great first impression!
                </Typography>
              </Box>
            </Box>
          )}
          <TextField
            fullWidth
            value={shareLink}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleCopyLink}
                    edge="end"
                    sx={{
                      color: linkCopied ? "#4caf50" : "#756DE9",
                      "&:hover": {
                        backgroundColor: "rgba(117, 109, 233, 0.1)",
                      },
                    }}
                    title={linkCopied ? "Copied!" : "Copy link"}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#f5f5f5",
              },
            }}
          />
          {linkCopied && (
            <Typography
              sx={{
                fontSize: "12px",
                color: "#4caf50",
                mt: 1,
                textAlign: "center",
              }}
            >
              Link copied to clipboard!
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ padding: "16px 24px", pb: 2 }}>
          <Button
            onClick={handleCloseShareModal}
            sx={{
              backgroundColor: "#756DE9",
              color: "#fff",
              borderRadius: "10px",
              textTransform: "capitalize",
              padding: "8px 24px",
              "&:hover": {
                backgroundColor: "#6352CC",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Bottom Sheet */}
      <Drawer
        anchor="bottom"
        open={shareBottomSheetOpen}
        onClose={handleCloseShareBottomSheet}
        PaperProps={{
          sx: {
            borderTopLeftRadius: "20px",
            borderTopRightRadius: "20px",
            maxHeight: "50vh",
          },
        }}
      >
        <Box sx={{ width: "100%", padding: "20px" }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: 600,
                color: "#1F2235",
              }}
            >
              Share your SeeVi
            </Typography>
            <IconButton onClick={handleCloseShareBottomSheet} sx={{ padding: "4px" }}>
              <CloseIcon sx={{ fontSize: "24px", color: "#666" }} />
            </IconButton>
          </Box>
          {!hasProfilePicture && (
            <Box
              sx={{
                mb: 2,
                borderRadius: "12px",
                background: "linear-gradient(135deg, rgba(117,109,233,0.12) 0%, rgba(117,109,233,0.06) 100%)",
                border: "1px solid rgba(117,109,233,0.25)",
                padding: "12px 14px",
                display: "flex",
                alignItems: "flex-start",
                gap: "10px",
              }}
            >
              <Box
                sx={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(117,109,233,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  fontSize: "16px",
                }}
              >
                📸
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    color: "#1F2235",
                    mb: 0.4,
                  }}
                >
                  Boost your visibility & trust
                </Typography>
                <Typography
                  sx={{
                    fontSize: "11px",
                    color: "#555555",
                    lineHeight: 1.5,
                    mb: 1,
                  }}
                >
                  Add a profile photo to make a great first impression and get more views!
                </Typography>
              </Box>
            </Box>
          )}
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleShareWhatsApp} sx={{ borderRadius: "10px", mb: 1 }}>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: "#25D366",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "20px",
                      fontWeight: "bold",
                    }}
                  >
                    WA
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary="WhatsApp"
                  primaryTypographyProps={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#1F2235",
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleShareFacebook} sx={{ borderRadius: "10px", mb: 1 }}>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      backgroundColor: "#1877F2",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "white",
                      fontSize: "18px",
                      fontWeight: "bold",
                    }}
                  >
                    f
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary="Facebook"
                  primaryTypographyProps={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#1F2235",
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleShareEmail} sx={{ borderRadius: "10px", mb: 1 }}>
                <ListItemIcon>
                  <Box
                    sx={{
                      width: "32px",
                      height: "32px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: "#f5f5f5",
                      borderRadius: "50%",
                    }}
                  >
                    <img src="images/Email_icon.png" alt="Email" width="20px" height="20px" />
                  </Box>
                </ListItemIcon>
                <ListItemText
                  primary="Email"
                  primaryTypographyProps={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: "#1F2235",
                  }}
                />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleCopyLink} sx={{ borderRadius: "10px" }}>
                <ListItemIcon>
                  <ContentCopyIcon sx={{ color: "#756DE9", fontSize: "24px" }} />
                </ListItemIcon>
                <ListItemText
                  primary={linkCopied ? "Copied!" : "Copy Link"}
                  primaryTypographyProps={{
                    fontSize: "16px",
                    fontWeight: 500,
                    color: linkCopied ? "#4caf50" : "#1F2235",
                  }}
                />
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default CompleteProfile;
